import React, {useEffect, useState, useMemo} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Ratings} from '@/components/ratings'
import Price from './price'
import {ProductTileImage} from './productTileImage'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {ProductMessaging} from './productMessaging'
import ProductTileSkeleton from './productTileSkeleton'
import AddToCart from '../add-to-cart'
import {Button as CTAButton} from '@/components/ui/button'
import {ProductContext} from '@/context/ProductContext'
import {ProductDataType} from '@/types/product'
import {cn} from '@/lib/utils'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {ProductQuickviewModal} from './productQuickviewModal'
import useMediaQuery from 'hec/hooks/useMediaQuery'

interface ProductTileCmsProps {
    product: ProductDataType
    isProductLoading?: boolean
    isAddToCart: boolean
    addedToCartPodId: string
    enableQuickView?: boolean
    enableProductMessaging?: boolean
    enableBasicView?: boolean
    className?: string
    style?: Record<string, string>
    'data-testid'?: string
    onOpenModal?: () => void
    lmnCallToAction?: string
    lmnBadgeColor?: string
}
import {createProductViewModel} from '../../helpers/createProductViewModel'
import {getImageHost} from './utils'

const ProductTileCms: React.FC<ProductTileCmsProps> = ({
    isProductLoading,
    product,
    isAddToCart,
    addedToCartPodId,
    enableQuickView,
    enableProductMessaging,
    enableBasicView,
    className,
    style,
    'data-testid': dataTestId,
    onOpenModal,
    lmnCallToAction,
    lmnBadgeColor,
    ...props
}) => {
    // Track which product/variant to fetch
    const [variantProductId, _setVariantProductId] = useState<string>(
        product.variation_id && product.variation_id !== '' ? product.variation_id : product.id
    )
    const [activeProduct, setActiveProduct] = useState<ProductDataType | null>(null)
    const [itemQuantity, setItemQuantity] = useState<number>(1)
    const [quickviewModalOpen, setQuickviewModalOpen] = useState(false)
    const [bundledProducts, setBundledProducts] = useState<unknown>(null)
    const [productIdToFetch, setProductIdToFetch] = useState<string>(variantProductId)
    const setVariantProductId = (productId: string) => {
        _setVariantProductId(productId)
        setProductIdToFetch(productId)
    }

    const {
        image_url,
        rating,
        number_of_reviews,
        name,
        price,
        url,
        c_badging,
        c_secondBadging,
        c_ownerClass,
        type
    } = product
    const isBundle = type?.bundle === true
    const badges = [c_badging, c_secondBadging].filter(Boolean)
    const isLMNProduct = c_ownerClass?.toLowerCase() === 'lmn'
    const siteId: string = getConfig().app.commerceAPI.parameters.siteId
    // Use the actual hostname, stripping any partner prefix (e.g. wex-fsa.devhec.com → fsa.devhec.com)
    const hostname =
        typeof window !== 'undefined' ? window.location.hostname : `${siteId.toLowerCase()}.com`
    const imageHost =
        getImageHost(hostname) == 'localhost'
            ? `${siteId.toLowerCase()}.com`
            : getImageHost(hostname)
    const imageUrlPath: string = typeof image_url === 'string' ? image_url : ''
    const productImageSrc = `https://${imageHost}${imageUrlPath}?sm=small`
    const isMobile = useMediaQuery('sm')
    const isTablet = useMediaQuery('lg')
    const {data: currentCustomer} = useCurrentCustomer()
    const registeredCustomerId = currentCustomer?.isRegistered && currentCustomer?.customerId
    const shouldQueryProductData = !product?.skeleton && (!!productIdToFetch || isBundle)
    const {
        isLoading: productDataIsLoading,
        error: productDataError,
        data: rawProductData
    } = useProduct(
        {
            parameters: {
                id: productIdToFetch,
                allImages: true
            }
        },
        {
            enabled: shouldQueryProductData,
            refetchOnMount: false
        }
    )

    // Use hydrated data from useProduct, fallback to product
    const productData: ProductDataType | undefined = rawProductData
        ? (rawProductData as unknown as ProductDataType)
        : undefined
    // Set activeProduct from hydrated useProduct data
    useEffect(() => {
        if (!productDataIsLoading && productData && productData.id) {
            setActiveProduct(
                createProductViewModel(
                    [{sourceType: 'scapi', subType: 'single', ...productData}],
                    'scapi'
                )
            )
        }
    }, [productData, productDataIsLoading])
    const memoizedBundledProducts = React.useMemo(() => {
        if (
            !productDataIsLoading &&
            rawProductData &&
            productIdToFetch === product.id &&
            isBundle
        ) {
            const bundledArray = Object.values(rawProductData.bundledProducts ?? {})
            return bundledArray.map((item: unknown) => {
                const prod = (item as {product?: unknown}).product
                if (prod && typeof prod === 'object') {
                    return {
                        product: createProductViewModel([{sourceType: 'scapi', ...prod}], 'scapi'),
                        quantity: item.quantity
                    }
                }
                return null
            })
        }
        return null
    }, [rawProductData, productDataIsLoading, productIdToFetch, product.id, isBundle])

    useEffect(() => {
        setBundledProducts(memoizedBundledProducts)
    }, [memoizedBundledProducts])
    if (productDataError) {
        console.error(`Error fetching product data for ID: ${variantProductId}`, productDataError)
    }

    return (
        <ProductContext.Provider
            value={{
                product,
                activeProduct,
                setVariantProductId,
                productImageSrc,
                quickviewModalOpen,
                setQuickviewModalOpen,
                isAddToCart,
                addedToCartPodId,
                registeredCustomerId,
                itemQuantity,
                setItemQuantity,
                bundledProducts,
                bundleProductLoading: productDataIsLoading && productIdToFetch === product.id,
                bundleProductError:
                    productDataError && productIdToFetch === product.id
                        ? productDataError
                        : undefined,
                setProductIdToFetch
            }}
        >
            <div
                className={cn('rounded-md p-1 bg-white flex flex-col h-full', className)}
                style={style}
                {...props}
            >
                {isProductLoading ? (
                    <ProductTileSkeleton data-testid="productTileCms-ProductTileSkeleton" />
                ) : (
                    <>
                        <ProductTileImage
                            badges={badges}
                            enableQuickView={enableQuickView}
                            lmnBadgeColor={lmnBadgeColor}
                            isLMNProduct={isLMNProduct}
                            data-testid="productTileCms-ProductTileImage"
                        />
                        <div className="flex mt-2 ">
                            {enableBasicView ? null : (
                                <Ratings
                                    size={isMobile ? 'small' : isTablet ? 'large' : 'medium'}
                                    rating={rating ?? 0}
                                    reviewCount={number_of_reviews}
                                    siteId={siteId}
                                    data-testid="productTileCms-Ratings"
                                />
                            )}
                        </div>
                        <a
                            href={url}
                            className={cn(
                                'text-emphasis font-grotesque font-medium mb-[.5rem] overflow-hidden mt-2 max-h-[2.4em] leading-[1.2]'
                            )}
                            style={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden'
                            }}
                        >
                            {name}
                        </a>

                        {isLMNProduct && (
                            <button
                                className="underline text-core-color-red-500 text-left"
                                onClick={onOpenModal}
                            >
                                What&apos;s an LMN ?
                            </button>
                        )}

                        {!isLMNProduct && (
                            <Price
                                className={cn('font-grotesque font-medium text-accent mb-[.5rem]')}
                                price={activeProduct?.price || price}
                                priceMax={product.priceMax}
                                siteId={siteId}
                                data-testid="productTileCms-Price"
                            />
                        )}
                        {!enableBasicView && !isLMNProduct ? (
                            <AddToCart
                                data-testid="productTileCms-AddToCart"
                                isProductTile={true}
                            />
                        ) : null}

                        {isLMNProduct && (
                            <CTAButton
                                ctaType="link"
                                variant="primary"
                                hrefValue={url}
                                className="w-full inline-block px-[0.125rem] pt-[.875rem] pb-[.875rem] text-center rounded-[.5rem] leading-tight mt-auto"
                            >
                                {lmnCallToAction}
                            </CTAButton>
                        )}
                        {enableProductMessaging ? (
                            <ProductMessaging
                                data-testid={dataTestId || 'productTileCms-ProductMessaging'}
                            />
                        ) : null}
                    </>
                )}
            </div>
            {quickviewModalOpen && (
                <ProductQuickviewModal data-testid="productTileCms-ProductQuickviewModal" />
            )}
        </ProductContext.Provider>
    )
}

export default ProductTileCms
